import {ActionList, type ActionListItemProps, Overlay, Portal} from '@primer/react'
import {SkeletonText} from '@primer/react/experimental'
import type React from 'react'
import {useId, useLayoutEffect, useRef, useState} from 'react'

import styles from './AutocompleteSuggestions.module.css'
import type {Suggestion, Suggestions, SuggestionsPlacement} from './types'
import {getSuggestionKey, getSuggestionValue} from './utils'

type AutoCompleteSuggestionsProps = {
  suggestions: Suggestions | null
  activeSuggestion?: Suggestion
  portalName?: string
  triggerCharCoords: {top: number; left: number; height: number}
  onClose: () => void
  onCommit: (suggestion: Suggestion) => void
  inputRef: React.RefObject<{focus(): void} | null>
  visible: boolean
  defaultPlacement: SuggestionsPlacement
  getSuggestionId: (suggestion: Suggestion) => string
  id: string
  menuTitle?: string
  role: 'menu' | 'listbox'
  tabInsertsSuggestions: boolean
}

const LoadingIndicator = () => (
  <>
    <ActionList.Item disabled>
      <SkeletonText />
    </ActionList.Item>
    <ActionList.Item disabled>
      <SkeletonText />
    </ActionList.Item>
    <ActionList.Item disabled>
      <SkeletonText />
    </ActionList.Item>
  </>
)

const SuggestionListItem = ({
  suggestion,
  onSelect,
  isActive,
  id,
  parentRole,
}: {
  suggestion: Suggestion
  onSelect: () => void
  isActive: boolean
  id: string
  parentRole: 'menu' | 'listbox'
}) => {
  const value = getSuggestionValue(suggestion)

  const sharedProps: React.LiHTMLAttributes<HTMLLIElement> & ActionListItemProps = {
    id,
    role: parentRole === 'menu' ? 'menuitem' : 'option',
    active: isActive,
    className: styles.suggestion,
    ['aria-selected']: parentRole === 'listbox' && isActive ? true : undefined,
    // onSelect/onClick won't work here; we want sto prevent default before focus leaves the input, so that focus remains
    // on the input. We don't need to handle key events here since that's done at the input level.
    onMouseDown: event => {
      event.preventDefault()
      onSelect()
    },
  }

  return typeof suggestion === 'string' ? (
    <ActionList.Item {...sharedProps}>{value}</ActionList.Item>
  ) : (
    suggestion.render({...sharedProps, children: value})
  )
}

/**
 * Renders an overlayed list at the given relative coordinates. Handles keyboard navigation
 * and accessibility concerns.
 */
export const AutocompleteSuggestions = ({
  suggestions,
  portalName,
  triggerCharCoords,
  onClose,
  onCommit,
  inputRef,
  visible,
  defaultPlacement,
  activeSuggestion,
  getSuggestionId,
  id,
  menuTitle,
  role,
  tabInsertsSuggestions,
}: AutoCompleteSuggestionsProps) => {
  const overlayRef = useRef<HTMLDivElement | null>(null)

  const [top, setTop] = useState(0)
  const [height, setHeight] = useState<string>('auto')
  useLayoutEffect(
    function recalculateTop() {
      // Reset height to auto for proper measurement
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHeight('auto')
      const overlayHeight = overlayRef.current?.offsetHeight ?? 0

      const belowOffset = triggerCharCoords.top + triggerCharCoords.height
      const wouldOverflowBelow = belowOffset + overlayHeight > window.innerHeight

      const aboveOffset = triggerCharCoords.top - overlayHeight
      const wouldOverflowAbove = aboveOffset < 0

      // Only override the default if it would overflow in the default direction and it would not overflow in the override direction
      let finalTopPosition: number = {
        below: wouldOverflowBelow && !wouldOverflowAbove ? aboveOffset : belowOffset,
        above: wouldOverflowAbove && !wouldOverflowBelow ? belowOffset : aboveOffset,
      }[defaultPlacement]

      // Sometimes the value can be NaN if layout is not available (ie, SSR or JSDOM)
      if (Number.isNaN(finalTopPosition)) finalTopPosition = 0

      // Ensure position is not negative (prevents overlay from appearing outside viewport)
      setTop(Math.max(0, finalTopPosition))

      // Handle case where overlay doesn't fit in either direction
      if (wouldOverflowBelow && wouldOverflowAbove) {
        const availableHeight =
          defaultPlacement === 'above'
            ? window.innerHeight + finalTopPosition // Space from top of overlay to bottom of viewport
            : window.innerHeight - finalTopPosition // Space from overlay position to bottom of viewport

        setHeight(`${availableHeight}px`)
      }
    },
    // this is a cheap effect and we want it to run when pretty much anything that could affect position changes
    [triggerCharCoords.top, triggerCharCoords.height, suggestions, visible, defaultPlacement],
  )

  const items = (
    <>
      {suggestions === 'loading' ? (
        <LoadingIndicator />
      ) : (
        suggestions?.map(suggestion => (
          <SuggestionListItem
            suggestion={suggestion}
            key={getSuggestionKey(suggestion)}
            id={getSuggestionId(suggestion)}
            onSelect={() => onCommit(suggestion)}
            isActive={activeSuggestion === suggestion}
            parentRole={role}
          />
        ))
      )}
    </>
  )
  const labelId = useId()
  // The Primer Overlay only calls `focus()` on this ref, so this cast is safe given the type enforcement for
  // `inputRef` (i.e. anything focusable will work)
  const typedInputRef = inputRef as unknown as React.RefObject<HTMLElement>

  /**
   * Even though we apply all the aria attributes, screen readers don't fully support this
   * dynamic use case and so they don't have a native way to indicate to the user when
   * there are suggestions available. So we use some hidden text with aria-live to politely
   * indicate what's available and how to use it.
   *
   * This text should be consistent and the important info should be first, because users
   * will hear it as they type - if they have heard the message before they should be able
   * to recognize it and quickly apply the first suggestion without listening to the rest
   * of the message.
   *
   * When screen reader users navigate using arrow keys, the `aria-activedescendant` will
   * change and will be read out so we don't need to handle that interaction here.
   */
  const description =
    !visible || !suggestions
      ? ''
      : suggestions === 'loading'
        ? 'Loading autocomplete suggestions…'
        : // It's important to include both Enter and Tab because we are telling the user that we are hijacking these keys:
          role === 'listbox'
          ? `${suggestions.length} autocomplete ${
              suggestions.length === 1 ? 'suggestion' : 'suggestions'
            } available; "${getSuggestionValue(suggestions[0]!)}" is highlighted. Press ${
              tabInsertsSuggestions ? 'Enter or Tab' : 'Enter'
            } to select.`
          : ''

  // Conditional rendering appears wrong at first - it means that we are reconstructing the
  // Combobox instance every time the suggestions appear. But this is what we want - otherwise
  // the textarea would always have the `combobox` role, which is incorrect (a textarea should
  // not technically ever be a combobox). We compromise by dynamically applying the combobox
  // role only when suggestions are available.
  return visible ? (
    <>
      <Overlay
        onEscape={onClose}
        onClickOutside={onClose}
        returnFocusRef={typedInputRef}
        preventFocusOnOpen
        portalContainerName={portalName}
        top={top}
        left={triggerCharCoords.left}
        ref={overlayRef}
        className={styles.Overlay_0}
      >
        <ActionList
          role={role}
          id={id}
          aria-label={menuTitle ? undefined : 'Autocomplete suggestions'}
          aria-labelledby={menuTitle ? labelId : undefined}
          className={styles.ActionList_0}
          style={{height}}
        >
          {menuTitle ? (
            <ActionList.Group>
              <ActionList.GroupHeading id={labelId}>{menuTitle}</ActionList.GroupHeading>
              {items}
            </ActionList.Group>
          ) : (
            items
          )}
        </ActionList>
      </Overlay>

      <Portal>
        {/* This should NOT be linked to the input with aria-describedby or screen readers may not read the live updates.
        The assertive live attribute ensures the suggestions are read instead of the input label, which voiceover will try to re-read when the role changes. */}
        <span aria-live="assertive" aria-atomic style={{clipPath: 'circle(0)', position: 'absolute'}}>
          {description}
        </span>
      </Portal>
    </>
  ) : (
    <></>
  )
}
